import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useSelector} from 'react-redux';
import {Modal, ModalBody, ModalFooter, ModalHeader} from 'reactstrap';

import Header from 'components/Header';
import LoadingIndicator from 'components/LoadingIndicator';
import Sidebar from 'components/Sidebar';
import {BASE_API_URL} from 'config';
import {makeSelectBasicUser} from 'reducers/selectors/userSelectors';
import {
  clearSubmittedUiTranslationChanges,
  clearUiTranslationRollbackAttempt,
  createUiTranslationPublicationFingerprint,
  createUiTranslationRollbackAttemptKey,
  createUiTranslationRollbackFingerprint,
  createUiTranslationWorkspace,
  createUiTranslationWorkspaceKey,
  loadUiTranslationWorkspace,
  normalizeUiTranslationWorkspace,
  prepareUiTranslationRollbackAttempt,
  prepareUiTranslationPublication,
  rebaseUiTranslationWorkspace,
  removeUiTranslationChanges,
  resolveUiTranslationConflicts,
  saveUiTranslationWorkspace,
  upsertUiTranslationChange,
  validateUiTranslationValue,
} from 'utils/uiTranslationWorkspace';
import {
  createUiTranslationIdempotencyKey,
  formatUiTranslationRequestError,
  getSafeUiTranslationExampleUrl,
  getUiTranslationCatalog,
  getUiTranslationErrorCode,
  getUiTranslationHistory,
  getUiTranslationHistoryDetail,
  normalizeUiTranslationConflict,
  publishUiTranslationChanges,
  rollbackUiTranslations,
} from './api';
import './style.scss';

export const UI_TRANSLATION_LOCALES = [
  {code: 'de', label: 'German', lang: 'de', direction: 'ltr'},
  {code: 'fr', label: 'French', lang: 'fr', direction: 'ltr'},
  {code: 'it', label: 'Italian', lang: 'it', direction: 'ltr'},
  {code: 'es', label: 'Spanish', lang: 'es', direction: 'ltr'},
  {code: 'pt', label: 'Portuguese (Portugal)', lang: 'pt-PT', direction: 'ltr'},
  {code: 'ja', label: 'Japanese', lang: 'ja', direction: 'ltr'},
  {code: 'cn', label: 'Chinese (Simplified)', lang: 'zh-CN', direction: 'ltr'},
  {code: 'ru', label: 'Russian', lang: 'ru', direction: 'ltr'},
  {code: 'ar', label: 'Arabic', lang: 'ar', direction: 'rtl'},
];

const UI_TRANSLATION_NAMESPACES = [{code: 'common', label: 'Common'}];
const basicUserSelector = makeSelectBasicUser();

const roleTitles = (roles) =>
  (Array.isArray(roles) ? roles : [])
    .map((role) => String(role?.title || '').toLowerCase())
    .filter(Boolean);

export const isUiTranslationAdmin = (roles) =>
  roleTitles(roles).includes('admin');

const currentLocale = (locale) =>
  UI_TRANSLATION_LOCALES.find((candidate) => candidate.code === locale) ||
  UI_TRANSLATION_LOCALES[0];

const normalizeSearch = (value) => String(value || '').trim().toLowerCase();

const asErrorObject = (value) =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {};

export const isUiTranslationStaleCatalogError = (error) => {
  if (
    error?.status !== 409 ||
    getUiTranslationErrorCode(error) !== 'UI_TRANSLATION_VALIDATION_FAILED'
  ) {
    return false;
  }

  const payload = asErrorObject(error?.payload);
  const unwrappedPayload = asErrorObject(
    payload.data && typeof payload.data === 'object' ? payload.data : payload
  );
  const nestedError = asErrorObject(unwrappedPayload.error);
  const details = asErrorObject(unwrappedPayload.details);
  const nestedDetails = asErrorObject(nestedError.details);
  const field = [
    error?.field,
    unwrappedPayload.field,
    details.field,
    nestedError.field,
    nestedDetails.field,
  ].find((candidate) => typeof candidate === 'string');

  return field === 'catalogVersion';
};

export const filterUiTranslationEntries = (
  entries,
  changes,
  {status, origin, context, query}
) => {
  const changeByKey = new Map(
    (Array.isArray(changes) ? changes : []).map((change) => [change.key, change])
  );
  const contextSearch = normalizeSearch(context);
  const freeSearch = normalizeSearch(query);

  return (Array.isArray(entries) ? entries : []).filter((entry) => {
    const change = changeByKey.get(entry.key);
    const localValue = change?.action === 'SET_VALUE' ? change.value : '';
    const statusMatches =
      !status ||
      status === 'all' ||
      (status === 'local' && Boolean(change)) ||
      (status === 'generated' && entry.status === 'GENERATED') ||
      (status === 'human' && entry.status === 'HUMAN_EDITED') ||
      (status === 'missing' && entry.status === 'MISSING_TRANSLATION') ||
      (status === 'review' && entry.reviewNeeded);
    const originMatches =
      !origin || origin === 'all' || entry.origin.toLowerCase() === origin;
    const contextValue = [entry.context, ...(entry.locations || [])]
      .join(' ')
      .toLowerCase();
    const contextMatches =
      !contextSearch || contextValue.includes(contextSearch);
    const freeValue = [
      entry.source,
      entry.key,
      entry.currentValue,
      localValue,
      entry.context,
      ...(entry.variables || []),
      ...(entry.locations || []),
    ]
      .join(' ')
      .toLowerCase();
    return (
      statusMatches &&
      originMatches &&
      contextMatches &&
      (!freeSearch || freeValue.includes(freeSearch))
    );
  });
};

const formatRevision = (revision) =>
  Number.isSafeInteger(revision) && revision > 0 ? revision : 'None';

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.valueOf()) ? value : date.toLocaleString('en-GB');
};

const formatActor = (actor) => {
  const label = actor?.label || 'Unknown actor';
  return actor?.type ? `${label} · ${actor.type}` : label;
};

const formatChangedKey = (change) => {
  if (typeof change === 'string') return change;
  return (
    change?.key ||
    change?.keyHash ||
    [change?.locale, change?.namespace].filter(Boolean).join(' · ') ||
    'Changed entry'
  );
};

const buildChangeBody = (change) => ({
  key: change.key,
  baseHash: change.baseHash,
  baseOwnership: change.baseOwnership,
  action: change.action,
  ...(change.action === 'SET_VALUE' ? {value: change.value} : {}),
});

const workspaceStorage = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const workspaceLockManager = () => {
  if (typeof navigator === 'undefined') return null;
  return navigator.locks || null;
};

const UiTranslations = () => {
  const basicUser = useSelector(basicUserSelector);
  const token = basicUser?.authToken;
  const userId = basicUser?._id || basicUser?.id || basicUser?.email;
  const admin = isUiTranslationAdmin(basicUser?.roles);
  const [locale, setLocale] = useState(UI_TRANSLATION_LOCALES[0].code);
  const [namespace, setNamespace] = useState(
    UI_TRANSLATION_NAMESPACES[0].code
  );
  const [filters, setFilters] = useState({
    status: 'all',
    origin: 'all',
    context: '',
    query: '',
  });
  const [catalog, setCatalog] = useState(null);
  const [catalogSelectionKey, setCatalogSelectionKey] = useState('');
  const [workspace, setWorkspace] = useState(null);
  const [workspaceKey, setWorkspaceKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [storageWarning, setStorageWarning] = useState('');
  const [storageUnavailable, setStorageUnavailable] = useState(false);
  const [workspaceConcurrencyConflict, setWorkspaceConcurrencyConflict] =
    useState(false);
  const [view, setView] = useState('editor');
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState('');
  const [conflicts, setConflicts] = useState([]);
  const [conflictContext, setConflictContext] = useState(null);
  const [conflictChoices, setConflictChoices] = useState({});
  const [conflictOpen, setConflictOpen] = useState(false);
  const [conflictError, setConflictError] = useState('');
  const [historyItems, setHistoryItems] = useState([]);
  const [historySelectionKey, setHistorySelectionKey] = useState('');
  const [historyCursor, setHistoryCursor] = useState('');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [historyDetail, setHistoryDetail] = useState(null);
  const [historyDetailSelectionKey, setHistoryDetailSelectionKey] = useState(
    ''
  );
  const [rollbackItem, setRollbackItem] = useState(null);
  const [rollbackAttemptConflict, setRollbackAttemptConflict] = useState(null);
  const [rollingBack, setRollingBack] = useState(false);
  const [rollbackError, setRollbackError] = useState('');
  const mountedRef = useRef(false);
  const catalogRequestRef = useRef(0);
  const historyRequestRef = useRef(0);
  const workspaceRef = useRef(null);
  const workspaceKeyRef = useRef('');
  const persistedWorkspacesRef = useRef(new Map());
  const workspaceSaveQueuesRef = useRef(new Map());
  const workspaceSaveSequenceRef = useRef(new Map());
  const workspaceConcurrencyConflictRef = useRef(false);
  const selectionKey = `${locale}:${namespace}`;
  const selectionKeyRef = useRef(selectionKey);
  selectionKeyRef.current = selectionKey;
  workspaceRef.current = workspace;
  workspaceKeyRef.current = workspaceKey;
  workspaceConcurrencyConflictRef.current = workspaceConcurrencyConflict;

  const localeDetails = currentLocale(locale);
  const catalogIsCurrent =
    catalogSelectionKey === selectionKey &&
    catalog?.locale === locale &&
    catalog?.namespace === namespace;
  const historyIsCurrent = historySelectionKey === selectionKey;
  const historyDetailIsCurrent = historyDetailSelectionKey === selectionKey;

  const persistWorkspace = useCallback(
    (nextWorkspace, key = workspaceKey) => {
      if (!nextWorkspace || !key) return false;
      const sequence = (workspaceSaveSequenceRef.current.get(key) || 0) + 1;
      workspaceSaveSequenceRef.current.set(key, sequence);
      if (key === workspaceKeyRef.current) {
        workspaceRef.current = nextWorkspace;
        setWorkspace(nextWorkspace);
      }
      const previous = workspaceSaveQueuesRef.current.get(key) || Promise.resolve();
      const queued = previous.catch(() => null).then(async () => {
        if (
          key === workspaceKeyRef.current &&
          workspaceConcurrencyConflictRef.current
        ) {
          return null;
        }
        const persisted = persistedWorkspacesRef.current.get(key) || nextWorkspace;
        const candidate = normalizeUiTranslationWorkspace({
          ...nextWorkspace,
          storageRevision: persisted.storageRevision,
          writeToken: persisted.writeToken,
        });
        const result = await saveUiTranslationWorkspace(
          workspaceStorage(),
          key,
          candidate,
          workspaceLockManager()
        );
        if (result.saved) {
          persistedWorkspacesRef.current.set(key, result.workspace);
          if (
            key === workspaceKeyRef.current &&
            sequence === workspaceSaveSequenceRef.current.get(key)
          ) {
            workspaceRef.current = result.workspace;
            setWorkspace(result.workspace);
            workspaceConcurrencyConflictRef.current = false;
            setWorkspaceConcurrencyConflict(false);
            setStorageUnavailable(false);
            setStorageWarning('');
          }
          return result.workspace;
        }
        if (key === workspaceKeyRef.current) {
          if (result.conflict) {
            workspaceConcurrencyConflictRef.current = true;
            setWorkspaceConcurrencyConflict(true);
            setPublishOpen(false);
            setStorageWarning(
              'This draft changed in another browser tab. Your local edits are still shown here, but publishing is disabled. Reload the page to review the latest saved draft.'
            );
          } else {
            setStorageUnavailable(true);
            setStorageWarning(
              result.lockUnavailable
                ? 'This browser cannot provide the cross-tab lock required for safe local drafts. Publishing and rollback are disabled.'
                : 'Local changes could not be saved in this browser. Publishing is disabled until browser storage is available.'
            );
          }
        }
        return null;
      });
      workspaceSaveQueuesRef.current.set(key, queued);
      return queued;
    },
    [workspaceKey]
  );

  const loadCatalog = useCallback(async () => {
    const requestLocale = locale;
    const requestNamespace = namespace;
    const requestSelectionKey = `${requestLocale}:${requestNamespace}`;
    if (!token || !userId) {
      setCatalog(null);
      setCatalogSelectionKey('');
      setWorkspace(null);
      setWorkspaceKey('');
      setError('An authenticated staff profile is required.');
      return;
    }
    const requestVersion = catalogRequestRef.current + 1;
    catalogRequestRef.current = requestVersion;
    setCatalog(null);
    setCatalogSelectionKey('');
    setWorkspace(null);
    setWorkspaceKey('');
    setLoading(true);
    setError('');

    try {
      const nextCatalog = await getUiTranslationCatalog(token, {
        locale: requestLocale,
        namespace: requestNamespace,
      });
      const nextWorkspaceKey = createUiTranslationWorkspaceKey({
        environment: BASE_API_URL,
        userId,
        locale: requestLocale,
        namespace: requestNamespace,
      });
      const loaded = await loadUiTranslationWorkspace(
        workspaceStorage(),
        nextWorkspaceKey,
        workspaceLockManager()
      );
      let nextWorkspace =
        loaded.workspace ||
        createUiTranslationWorkspace({
          catalogVersion: nextCatalog.catalogVersion,
          baseRevision: nextCatalog.releaseRevision,
        });
      let nextConflicts = [];
      let rebaseMessage = '';
      let workspacePersistenceConflict = false;
      let workspacePersistenceError = false;

      if (
        loaded.workspace &&
        (loaded.workspace.catalogVersion !== nextCatalog.catalogVersion ||
          loaded.workspace.baseRevision !== nextCatalog.releaseRevision)
      ) {
        const rebased = rebaseUiTranslationWorkspace(loaded.workspace, {
          catalogVersion: nextCatalog.catalogVersion,
          baseRevision: nextCatalog.releaseRevision,
          entries: nextCatalog.entries,
        });
        nextWorkspace = rebased.workspace;
        nextConflicts = rebased.conflicts;
        rebaseMessage = nextConflicts.length
          ? 'Production changed while this browser had local work. Resolve the affected keys before publishing.'
          : `Local work was rebased onto revision ${nextCatalog.releaseRevision}.`;
      }

      if (
        mountedRef.current &&
        requestVersion === catalogRequestRef.current &&
        requestSelectionKey === selectionKeyRef.current
      ) {
        setCatalog(nextCatalog);
        setCatalogSelectionKey(requestSelectionKey);
        setWorkspaceKey(nextWorkspaceKey);
        workspaceKeyRef.current = nextWorkspaceKey;
        setWorkspace(nextWorkspace);
        workspaceRef.current = nextWorkspace;
        persistedWorkspacesRef.current.set(nextWorkspaceKey, nextWorkspace);
        setNotice(rebaseMessage);
        setConflicts(nextConflicts);
        setConflictContext(
          nextConflicts.length
            ? {
                latestRevision: nextCatalog.releaseRevision,
                catalogVersion: nextCatalog.catalogVersion,
              }
            : null
        );
        setConflictChoices({});
        setConflictOpen(false);
        setStorageWarning('');
        setStorageUnavailable(false);
        workspaceConcurrencyConflictRef.current = false;
        setWorkspaceConcurrencyConflict(false);
        if (loaded.discarded) {
          setStorageWarning(
            'A malformed, oversized, or expired local draft was safely discarded.'
          );
        } else if (loaded.lockUnavailable) {
          setStorageUnavailable(true);
          setStorageWarning(
            'This browser cannot provide the cross-tab lock required for safe local drafts. Publishing and rollback are disabled.'
          );
        } else if (loaded.storageError) {
          setStorageUnavailable(true);
          setStorageWarning(
            'Browser storage is unavailable. Publishing is disabled so an ambiguous retry cannot lose its idempotency key.'
          );
        } else if (nextWorkspace?.changes.length) {
          const saved = await saveUiTranslationWorkspace(
            workspaceStorage(),
            nextWorkspaceKey,
            nextWorkspace,
            workspaceLockManager()
          );
          if (saved.saved) {
            nextWorkspace = saved.workspace;
            persistedWorkspacesRef.current.set(nextWorkspaceKey, nextWorkspace);
            workspaceRef.current = nextWorkspace;
            setWorkspace(nextWorkspace);
          } else if (saved.conflict) {
            workspacePersistenceConflict = true;
            workspaceConcurrencyConflictRef.current = true;
            setWorkspaceConcurrencyConflict(true);
            setStorageWarning(
              'This draft changed in another browser tab while it was loading. Publishing is disabled. Reload the page to review the latest saved draft.'
            );
          } else {
            workspacePersistenceError = true;
            setStorageUnavailable(true);
            setStorageWarning(
              saved.lockUnavailable
                ? 'This browser cannot provide the cross-tab lock required for safe local drafts. Publishing and rollback are disabled.'
                : 'Local changes could not be saved in this browser. Publishing is disabled until browser storage is available.'
            );
          }
        }
        if (!workspacePersistenceConflict && !workspacePersistenceError) {
          workspaceConcurrencyConflictRef.current = false;
          setWorkspaceConcurrencyConflict(false);
        }
      }
    } catch (requestError) {
      if (
        mountedRef.current &&
        requestVersion === catalogRequestRef.current &&
        requestSelectionKey === selectionKeyRef.current
      ) {
        setError(formatUiTranslationRequestError(requestError));
      }
    } finally {
      if (
        mountedRef.current &&
        requestVersion === catalogRequestRef.current &&
        requestSelectionKey === selectionKeyRef.current
      ) {
        setLoading(false);
      }
    }
  }, [locale, namespace, token, userId]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      catalogRequestRef.current += 1;
      historyRequestRef.current += 1;
    };
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    if (!workspaceKey || typeof window === 'undefined') return undefined;
    const handleExternalWorkspaceChange = (event) => {
      if (
        event.key !== workspaceKey ||
        event.storageArea !== workspaceStorage()
      ) {
        return;
      }
      setWorkspaceConcurrencyConflict(true);
      workspaceConcurrencyConflictRef.current = true;
      setPublishOpen(false);
      setStorageWarning(
        'This draft changed in another browser tab. Your local edits are still shown here, but publishing is disabled. Reload the page to review the latest saved draft.'
      );
    };
    window.addEventListener('storage', handleExternalWorkspaceChange);
    return () =>
      window.removeEventListener('storage', handleExternalWorkspaceChange);
  }, [workspaceKey]);

  const changeSelection = (nextLocale, nextNamespace) => {
    const nextSelectionKey = `${nextLocale}:${nextNamespace}`;
    if (nextSelectionKey === selectionKeyRef.current) return;
    selectionKeyRef.current = nextSelectionKey;
    catalogRequestRef.current += 1;
    historyRequestRef.current += 1;
    setCatalog(null);
    setCatalogSelectionKey('');
    setWorkspace(null);
    setWorkspaceKey('');
    setError('');
    setNotice('');
    setPublishOpen(false);
    setPublishError('');
    setConflicts([]);
    setConflictContext(null);
    setConflictChoices({});
    setConflictOpen(false);
    setHistoryItems([]);
    setHistorySelectionKey('');
    setHistoryCursor('');
    setHistoryLoading(false);
    setHistoryError('');
    setHistoryDetail(null);
    setHistoryDetailSelectionKey('');
    setRollbackItem(null);
    setRollbackError('');
    setView('editor');
    setLoading(true);
    setLocale(nextLocale);
    setNamespace(nextNamespace);
  };

  const changeByKey = useMemo(
    () =>
      new Map(
        (workspace?.changes || []).map((change) => [change.key, change])
      ),
    [workspace]
  );
  const entryByKey = useMemo(
    () => new Map((catalog?.entries || []).map((entry) => [entry.key, entry])),
    [catalog]
  );

  const validationErrors = useMemo(() => {
    const errors = new Map();
    (workspace?.changes || []).forEach((change) => {
      const entry = entryByKey.get(change.key);
      if (!entry) {
        errors.set(change.key, 'This key is no longer in the current catalog.');
      } else if (
        change.action === 'RESET_TO_GENERATED' &&
        !entry.generatedBaselineAvailable
      ) {
        errors.set(
          change.key,
          'No current generated translation is available for this key.'
        );
      } else if (change.action === 'SET_VALUE') {
        const message = validateUiTranslationValue(entry, change.value);
        if (message) errors.set(change.key, message);
      }
    });
    return errors;
  }, [entryByKey, workspace]);

  const visibleEntries = useMemo(
    () =>
      filterUiTranslationEntries(
        catalog?.entries || [],
        workspace?.changes || [],
        filters
      ),
    [catalog, filters, workspace]
  );

  const summary = useMemo(() => {
    const entries = catalog?.entries || [];
    return {
      total: entries.length,
      missing: entries.filter(
        (entry) => entry.status === 'MISSING_TRANSLATION'
      ).length,
      review: entries.filter((entry) => entry.reviewNeeded).length,
      local: workspace?.changes.length || 0,
    };
  }, [catalog, workspace]);

  const hasUnresolvedConflicts = conflicts.length > 0;
  const canPublish = Boolean(
    catalogIsCurrent &&
      catalog?.initialReleaseComplete &&
      workspace?.changes.length &&
      !validationErrors.size &&
      !hasUnresolvedConflicts &&
      !loading &&
      !publishing &&
      !storageUnavailable &&
      !workspaceConcurrencyConflict
  );

  const updateFilter = (key, value) => {
    setFilters((current) => ({...current, [key]: value}));
  };

  const clearFilters = () =>
    setFilters({status: 'all', origin: 'all', context: '', query: ''});

  const handleValueChange = (entry, value) => {
    const currentWorkspace = workspaceRef.current || workspace;
    if (
      !catalogIsCurrent ||
      !currentWorkspace ||
      !catalog?.initialReleaseComplete
    ) {
      return;
    }
    const existing = changeByKey.get(entry.key);
    const nextWorkspace =
      value === entry.currentValue
        ? removeUiTranslationChanges(currentWorkspace, [entry.key])
        : upsertUiTranslationChange(currentWorkspace, {
            key: entry.key,
            action: 'SET_VALUE',
            value,
            baseHash: existing?.baseHash ?? entry.currentValueHash,
            baseOwnership: existing?.baseOwnership ?? entry.ownership,
            sourceHash: existing?.sourceHash || entry.sourceHash,
          });
    persistWorkspace(nextWorkspace);
    setConflictChoices((current) => {
      if (!Object.prototype.hasOwnProperty.call(current, entry.key)) {
        return current;
      }
      const next = {...current};
      delete next[entry.key];
      return next;
    });
    setConflictError('');
    setNotice('');
    setPublishError('');
  };

  const handleResetField = (entry) => {
    const currentWorkspace = workspaceRef.current || workspace;
    if (!catalogIsCurrent || !currentWorkspace) return;
    const nextWorkspace = removeUiTranslationChanges(currentWorkspace, [entry.key]);
    persistWorkspace(nextWorkspace);
    setConflicts((current) =>
      current.filter((conflict) => conflict.key !== entry.key)
    );
    setConflictChoices((current) => {
      const next = {...current};
      delete next[entry.key];
      return next;
    });
    setNotice('The field now matches the current production value.');
  };

  const handleResetToGenerated = (entry) => {
    const currentWorkspace = workspaceRef.current || workspace;
    if (
      !currentWorkspace ||
      !catalogIsCurrent ||
      !catalog?.initialReleaseComplete ||
      !entry.generatedBaselineAvailable
    ) {
      return;
    }
    const existing = changeByKey.get(entry.key);
    const nextWorkspace = upsertUiTranslationChange(currentWorkspace, {
      key: entry.key,
      action: 'RESET_TO_GENERATED',
      baseHash: existing?.baseHash ?? entry.currentValueHash,
      baseOwnership: existing?.baseOwnership ?? entry.ownership,
      sourceHash: existing?.sourceHash || entry.sourceHash,
    });
    persistWorkspace(nextWorkspace);
    setConflictChoices((current) => {
      if (!Object.prototype.hasOwnProperty.call(current, entry.key)) {
        return current;
      }
      const next = {...current};
      delete next[entry.key];
      return next;
    });
    setConflictError('');
    setNotice(
      'The generated ownership reset is pending locally and has not changed production.'
    );
  };

  const discardChanges = () => {
    const currentWorkspace = workspaceRef.current || workspace;
    if (!catalogIsCurrent || !currentWorkspace?.changes.length) return;
    if (
      typeof window !== 'undefined' &&
      !window.confirm(
        `Discard all ${currentWorkspace.changes.length} local UI translation changes?`
      )
    ) {
      return;
    }
    const nextWorkspace = removeUiTranslationChanges(
      currentWorkspace,
      currentWorkspace.changes.map((change) => change.key)
    );
    persistWorkspace(nextWorkspace);
    setConflicts([]);
    setConflictContext(null);
    setConflictChoices({});
    setConflictOpen(false);
    setNotice('Local changes were discarded. Production was not changed.');
  };

  const enrichConflicts = (serverConflicts, changes) =>
    serverConflicts.map((conflict) => {
      const entry = entryByKey.get(conflict.key);
      const change = changes.find((candidate) => candidate.key === conflict.key);
      return {
        ...conflict,
        source: conflict.source || entry?.source || '',
        sourceHash: conflict.sourceHash || entry?.sourceHash || '',
        localValue:
          conflict.hasLocalValue
            ? conflict.localValue
            : change?.action === 'SET_VALUE'
              ? change.value
              : 'Reset to generated translation',
        removedFromCatalog: !entry,
      };
    });

  const submitPublication = async (workspaceToPublish) => {
    const changes = workspaceToPublish?.changes || [];
    if (!catalogIsCurrent || !workspaceKey || !workspaceToPublish) {
      return;
    }
    if (!changes.length) return;
    if (workspaceConcurrencyConflict) {
      setPublishError(
        'Publishing is disabled because this draft changed in another browser tab. Reload and review the latest saved draft.'
      );
      return;
    }
    const publicationSelectionKey = selectionKey;
    const body = {
      locale,
      namespace,
      catalogVersion: workspaceToPublish.catalogVersion,
      baseRevision: workspaceToPublish.baseRevision,
      changes: changes.map(buildChangeBody),
    };
    const fingerprint = createUiTranslationPublicationFingerprint(body);
    const prepared = prepareUiTranslationPublication(
      workspaceToPublish,
      fingerprint,
      createUiTranslationIdempotencyKey
    );
    const persistedPrepared =
      prepared.workspace && (await persistWorkspace(prepared.workspace));
    if (!persistedPrepared) {
      setPublishError(
        workspaceConcurrencyConflict
          ? 'This draft changed in another browser tab. Nothing was sent.'
          : 'The retry key could not be saved in this browser. Nothing was sent.'
      );
      return;
    }

    setPublishing(true);
    setPublishError('');
    setError('');
    try {
      const response = await publishUiTranslationChanges(
        token,
        body,
        prepared.idempotencyKey
      );
      if (
        !mountedRef.current ||
        publicationSelectionKey !== selectionKeyRef.current
      ) {
        return;
      }
      const submittedKeys = changes.map((change) => change.key);
      const remaining = clearSubmittedUiTranslationChanges(
        persistedPrepared,
        submittedKeys,
        {
          catalogVersion: response.catalogVersion,
          baseRevision: response.revision,
        }
      );
      const persistedRemaining = await persistWorkspace(remaining);
      if (!persistedRemaining) {
        setPublishError(
          'The publication succeeded, but another tab changed the local draft before cleanup. Reload to review the saved draft.'
        );
        return;
      }
      setPublishOpen(false);
      setConflicts([]);
      setConflictContext(null);
      setConflictChoices({});
      setConflictOpen(false);
      await loadCatalog();
      if (mountedRef.current) {
        setNotice(
          `${submittedKeys.length} UI translation change${
            submittedKeys.length === 1 ? '' : 's'
          } published in revision ${response.revision}.`
        );
      }
    } catch (requestError) {
      if (
        !mountedRef.current ||
        publicationSelectionKey !== selectionKeyRef.current
      ) {
        return;
      }
      const conflict = normalizeUiTranslationConflict(requestError);
      const errorCode = getUiTranslationErrorCode(requestError);
      if (isUiTranslationStaleCatalogError(requestError)) {
        const invalidated = normalizeUiTranslationWorkspace({
          ...persistedPrepared,
          pendingPublication: null,
        });
        if (await persistWorkspace(invalidated)) {
          setPublishOpen(false);
          await loadCatalog();
          if (
            mountedRef.current &&
            publicationSelectionKey === selectionKeyRef.current
          ) {
            setNotice(
              'The UI translation catalog changed before publication. Local work was reloaded against the latest catalog; review it before publishing again.'
            );
          }
        }
      } else if (
        errorCode === 'UI_TRANSLATION_CONFLICT' &&
        conflict.conflicts.length
      ) {
        const invalidated = normalizeUiTranslationWorkspace({
          ...persistedPrepared,
          pendingPublication: null,
        });
        await persistWorkspace(invalidated);
        setConflicts(enrichConflicts(conflict.conflicts, changes));
        setConflictContext({
          latestRevision: conflict.latestRevision,
          catalogVersion:
            conflict.catalogVersion || workspaceToPublish.catalogVersion,
        });
        setConflictChoices({});
        setConflictError('');
        setPublishOpen(false);
        setConflictOpen(true);
      } else if (
        errorCode === 'UI_TRANSLATION_PUBLICATION_CONFLICT' ||
        (errorCode === 'UI_TRANSLATION_CONFLICT' &&
          !conflict.conflicts.length)
      ) {
        const invalidated = normalizeUiTranslationWorkspace({
          ...persistedPrepared,
          pendingPublication: null,
        });
        if (await persistWorkspace(invalidated)) {
          setPublishOpen(false);
          await loadCatalog();
          if (mountedRef.current) {
            setNotice(
              'Production changed before this publication completed. Local work was reloaded against the latest revision; review it before publishing again.'
            );
          }
        }
      } else {
        setPublishError(
          formatUiTranslationRequestError(requestError, 'publish')
        );
      }
    } finally {
      if (mountedRef.current) setPublishing(false);
    }
  };

  const openPublishConfirmation = () => {
    if (!canPublish) return;
    setPublishError('');
    setPublishOpen(true);
  };

  const resolveAndPublish = async () => {
    if (!catalogIsCurrent || !workspace || !conflictContext) return;
    const resolved = resolveUiTranslationConflicts(
      workspace,
      conflicts,
      conflictChoices,
      {
        catalogVersion: conflictContext.catalogVersion,
        baseRevision: conflictContext.latestRevision,
      }
    );
    if (resolved.unresolvedKeys.length) {
      setConflictError('Choose an option for every conflict.');
      return;
    }
    const persistedResolved = await persistWorkspace(resolved.workspace);
    if (!persistedResolved) return;
    setConflicts([]);
    setConflictChoices({});
    setConflictContext(null);
    setConflictOpen(false);
    setConflictError('');
    if (!persistedResolved.changes.length) {
      await loadCatalog();
      if (mountedRef.current) {
        setNotice('Conflicts were resolved using the current production values.');
      }
      return;
    }
    setPublishOpen(true);
    await submitPublication(persistedResolved);
  };

  const loadHistory = useCallback(
    async (cursor = '') => {
      if (!token) return;
      const requestLocale = locale;
      const requestNamespace = namespace;
      const requestSelectionKey = `${requestLocale}:${requestNamespace}`;
      const requestVersion = historyRequestRef.current + 1;
      historyRequestRef.current = requestVersion;
      setHistoryLoading(true);
      setHistoryError('');
      setHistoryDetail(null);
      setHistoryDetailSelectionKey('');
      if (!cursor) {
        setHistoryItems([]);
        setHistorySelectionKey('');
        setHistoryCursor('');
      }
      try {
        const response = await getUiTranslationHistory(token, {
          locale: requestLocale,
          namespace: requestNamespace,
          cursor,
        });
        if (
          mountedRef.current &&
          requestVersion === historyRequestRef.current &&
          requestSelectionKey === selectionKeyRef.current
        ) {
          setHistoryItems((current) =>
            cursor ? [...current, ...response.items] : response.items
          );
          setHistorySelectionKey(requestSelectionKey);
          setHistoryCursor(response.nextCursor);
        }
      } catch (requestError) {
        if (
          mountedRef.current &&
          requestVersion === historyRequestRef.current &&
          requestSelectionKey === selectionKeyRef.current
        ) {
          setHistoryError(
            formatUiTranslationRequestError(requestError, 'history')
          );
        }
      } finally {
        if (
          mountedRef.current &&
          requestVersion === historyRequestRef.current &&
          requestSelectionKey === selectionKeyRef.current
        ) {
          setHistoryLoading(false);
        }
      }
    },
    [locale, namespace, token]
  );

  const openHistory = () => {
    setView('history');
    setHistoryDetail(null);
    setHistoryDetailSelectionKey('');
    loadHistory();
  };

  const returnToEditor = () => {
    historyRequestRef.current += 1;
    setHistoryLoading(false);
    setHistoryDetail(null);
    setHistoryDetailSelectionKey('');
    setView('editor');
  };

  const returnToHistory = () => {
    historyRequestRef.current += 1;
    setHistoryLoading(false);
    setHistoryDetail(null);
    setHistoryDetailSelectionKey('');
    setView('history');
  };

  const openHistoryDetail = async (item) => {
    if (!historyIsCurrent || historyLoading) return;
    const requestSelectionKey = selectionKey;
    const requestVersion = historyRequestRef.current + 1;
    historyRequestRef.current = requestVersion;
    setHistoryLoading(true);
    setHistoryError('');
    setHistoryDetail(null);
    setHistoryDetailSelectionKey('');
    try {
      const detail = await getUiTranslationHistoryDetail(
        token,
        item.revision,
        {locale, namespace}
      );
      if (
        mountedRef.current &&
        requestVersion === historyRequestRef.current &&
        requestSelectionKey === selectionKeyRef.current
      ) {
        setHistoryDetail(detail);
        setHistoryDetailSelectionKey(requestSelectionKey);
        setView('history-detail');
      }
    } catch (requestError) {
      if (
        mountedRef.current &&
        requestVersion === historyRequestRef.current &&
        requestSelectionKey === selectionKeyRef.current
      ) {
        setHistoryError(
          formatUiTranslationRequestError(requestError, 'history')
        );
      }
    } finally {
      if (
        mountedRef.current &&
        requestVersion === historyRequestRef.current &&
        requestSelectionKey === selectionKeyRef.current
      ) {
        setHistoryLoading(false);
      }
    }
  };

  const openRollback = (item) => {
    if (
      !admin ||
      !catalogIsCurrent ||
      !historyIsCurrent ||
      historyLoading ||
      storageUnavailable ||
      !catalog?.initialReleaseComplete
    ) {
      return;
    }
    setRollbackError('');
    setRollbackAttemptConflict(null);
    setRollbackItem(item);
  };

  const confirmRollback = async () => {
    if (
      !rollbackItem ||
      !admin ||
      !catalogIsCurrent ||
      !historyIsCurrent ||
      storageUnavailable ||
      !catalog?.initialReleaseComplete
    ) {
      return;
    }
    const body = {
      locale,
      targetRevision: rollbackItem.revision,
      catalogVersion: catalog.catalogVersion,
    };
    const rollbackSelectionKey = selectionKey;
    const storage = workspaceStorage();
    const attemptKey = createUiTranslationRollbackAttemptKey({
      environment: BASE_API_URL,
      userId,
      locale,
    });
    const prepared = await prepareUiTranslationRollbackAttempt(
      storage,
      attemptKey,
      createUiTranslationRollbackFingerprint(body),
      createUiTranslationIdempotencyKey,
      workspaceLockManager()
    );
    if (prepared.lockUnavailable) {
      setStorageUnavailable(true);
      setStorageWarning(
        'This browser cannot provide the cross-tab lock required for safe local drafts. Publishing and rollback are disabled.'
      );
      setRollbackError(
        'Rollback is disabled because this browser cannot safely coordinate retry state across tabs.'
      );
      return;
    }
    if (prepared.conflict) {
      setRollbackAttemptConflict({attemptKey, attempt: prepared.attempt});
      setStorageWarning(
        'A different rollback attempt is active in another browser tab. It was not replaced.'
      );
      setRollbackError(
        'Rollback is blocked because another tab is retrying a different rollback. Reload before trying again.'
      );
      return;
    }
    if (!prepared.saved || !prepared.attempt) {
      setStorageUnavailable(true);
      setStorageWarning(
        'Browser storage is unavailable. Rollback is disabled so an ambiguous retry cannot create a duplicate revision.'
      );
      setRollbackError(
        'The rollback retry key could not be saved in this browser. Nothing was sent.'
      );
      return;
    }
    setRollingBack(true);
    setRollbackError('');
    try {
      const response = await rollbackUiTranslations(
        token,
        body,
        prepared.attempt.idempotencyKey
      );
      const cleared = await clearUiTranslationRollbackAttempt(
        storage,
        attemptKey,
        prepared.attempt,
        workspaceLockManager()
      );
      if (cleared.conflict) {
        setStorageWarning(
          'A newer rollback attempt exists in another browser tab and was not cleared.'
        );
      }
      setRollbackAttemptConflict(null);
      if (
        !mountedRef.current ||
        rollbackSelectionKey !== selectionKeyRef.current
      ) {
        return;
      }
      setRollbackItem(null);
      await loadCatalog();
      if (view === 'history') await loadHistory();
      if (
        mountedRef.current &&
        rollbackSelectionKey === selectionKeyRef.current
      ) {
        setNotice(
          `${localeDetails.label} was restored from revision ${body.targetRevision} in new revision ${response.revision}.`
        );
      }
    } catch (requestError) {
      const staleCatalog = isUiTranslationStaleCatalogError(requestError);
      const explicitClientRejection =
        Number.isInteger(requestError?.status) &&
        requestError.status >= 400 &&
        requestError.status < 500 &&
        requestError.status !== 408;
      if (staleCatalog || explicitClientRejection) {
        const cleared = await clearUiTranslationRollbackAttempt(
          storage,
          attemptKey,
          prepared.attempt,
          workspaceLockManager()
        );
        if (cleared.conflict) {
          setStorageWarning(
            'A newer rollback attempt exists in another browser tab and was not cleared.'
          );
        }
      }
      if (
        !mountedRef.current ||
        rollbackSelectionKey !== selectionKeyRef.current
      ) {
        return;
      }
      if (staleCatalog) {
        setRollbackItem(null);
        setRollbackAttemptConflict(null);
        setRollbackError('');
        await loadCatalog();
        if (
          mountedRef.current &&
          rollbackSelectionKey === selectionKeyRef.current
        ) {
          setNotice(
            'The UI translation catalog changed before rollback. The latest catalog was loaded; review the current state before trying again.'
          );
        }
      } else {
        setRollbackError(
          formatUiTranslationRequestError(requestError, 'rollback')
        );
      }
    } finally {
      if (mountedRef.current) setRollingBack(false);
    }
  };

  const clearConflictingRollbackAttempt = async () => {
    if (!rollbackAttemptConflict) return;
    if (
      typeof window !== 'undefined' &&
      !window.confirm(
        'Clear only this browser retry state? First check revision history: if the earlier request reached production, retrying may create another rollback revision.'
      )
    ) {
      return;
    }
    const cleared = await clearUiTranslationRollbackAttempt(
      workspaceStorage(),
      rollbackAttemptConflict.attemptKey,
      rollbackAttemptConflict.attempt,
      workspaceLockManager()
    );
    if (!cleared.cleared) {
      setRollbackError(
        cleared.conflict
          ? 'The retry state changed again in another tab and was not cleared.'
          : 'The local retry state could not be cleared safely.'
      );
      return;
    }
    setRollbackAttemptConflict(null);
    setRollbackError(
      'Local retry state cleared. Review revision history before retrying this rollback.'
    );
    setStorageWarning(
      'Only this browser retry state was cleared; production was not changed.'
    );
  };

  const renderEditor = () => (
    <>
      <section className="ui-translations__filters" aria-label="Translation filters">
        <label>
          Language
          <select
            value={locale}
            onChange={(event) =>
              changeSelection(event.target.value, namespace)
            }
            disabled={loading || publishing}
          >
            {UI_TRANSLATION_LOCALES.map((option) => (
              <option key={option.code} value={option.code}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Section
          <select
            value={namespace}
            onChange={(event) => changeSelection(locale, event.target.value)}
            disabled={loading || publishing}
          >
            {UI_TRANSLATION_NAMESPACES.map((option) => (
              <option key={option.code} value={option.code}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Status
          <select
            value={filters.status}
            onChange={(event) => updateFilter('status', event.target.value)}
          >
            <option value="all">All statuses</option>
            <option value="generated">Generated</option>
            <option value="human">Human edited</option>
            <option value="missing">Missing translation</option>
            <option value="review">Review needed</option>
            <option value="local">Local change</option>
          </select>
        </label>
        <label>
          Origin
          <select
            value={filters.origin}
            onChange={(event) => updateFilter('origin', event.target.value)}
          >
            <option value="all">All origins</option>
            <option value="generated">Generated</option>
            <option value="human">Human</option>
            <option value="missing">Missing</option>
          </select>
        </label>
        <label>
          Context or location
          <input
            type="search"
            value={filters.context}
            onChange={(event) => updateFilter('context', event.target.value)}
            placeholder="Header, breadcrumb, footer…"
          />
        </label>
        <label>
          Search
          <input
            type="search"
            value={filters.query}
            onChange={(event) => updateFilter('query', event.target.value)}
            placeholder="English, key, value…"
          />
        </label>
      </section>

      {catalog && (
        <p className="ui-translations__summary" role="status">
          {summary.total} entries · {summary.missing} missing · {summary.review}{' '}
          review needed · {summary.local} local change
          {summary.local === 1 ? '' : 's'}
        </p>
      )}

      {loading && !catalog && (
        <div className="ui-translations__loading" role="status">
          <LoadingIndicator />
          <span>Loading UI translations…</span>
        </div>
      )}

      {catalog && visibleEntries.length > 0 && (
        <div className="ui-translations__table-wrap">
          <table className="ui-translations__table">
            <thead>
              <tr>
                <th scope="col">English and context</th>
                <th scope="col">Current live</th>
                <th scope="col">{localeDetails.label}</th>
                <th scope="col">Status and actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleEntries.map((entry) => {
                const change = changeByKey.get(entry.key);
                const editableValue =
                  change?.action === 'SET_VALUE'
                    ? change.value
                    : entry.currentValue;
                const fieldError = validationErrors.get(entry.key);
                const exampleUrl = getSafeUiTranslationExampleUrl(
                  entry.examplePath
                );
                const resetPending =
                  change?.action === 'RESET_TO_GENERATED';
                return (
                  <tr
                    key={entry.key}
                    className={change ? 'ui-translations__row--changed' : ''}
                    data-testid={`translation-row-${entry.key}`}
                  >
                    <td data-label="English and context">
                      <strong className="ui-translations__source" lang="en" dir="ltr">
                        {entry.source}
                      </strong>
                      <code>{entry.key}</code>
                      {entry.context && <p>{entry.context}</p>}
                      <div className="ui-translations__metadata-line">
                        <span>Variables</span>
                        {entry.variables.length ? (
                          <span className="ui-translations__tokens">
                            {entry.variables.map((variable, index) => (
                              <code key={`${variable}-${index}`}>
                                {`{{${variable}}}`}
                              </code>
                            ))}
                          </span>
                        ) : (
                          <span>None</span>
                        )}
                      </div>
                      {entry.locations.length > 0 && (
                        <div className="ui-translations__locations">
                          <span>Used in</span>
                          <ul>
                            {entry.locations.map((location, index) => (
                              <li key={`${location}-${index}`}>{location}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {exampleUrl && (
                        <a
                          href={exampleUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open example page ↗
                        </a>
                      )}
                    </td>
                    <td data-label="Current live">
                      {entry.currentValue ? (
                        <span lang={localeDetails.lang} dir={localeDetails.direction}>
                          {entry.currentValue}
                        </span>
                      ) : (
                        <>
                          <span lang="en" dir="ltr">{entry.source}</span>
                          <small>English fallback</small>
                        </>
                      )}
                    </td>
                    <td data-label={localeDetails.label}>
                      <label className="ui-translations__field-label" htmlFor={`translation-${entry.key}`}>
                        Editable value
                      </label>
                      <textarea
                        id={`translation-${entry.key}`}
                        data-testid={`translation-input-${entry.key}`}
                        value={editableValue}
                        onChange={(event) =>
                          handleValueChange(entry, event.target.value)
                        }
                        rows={4}
                        lang={localeDetails.lang}
                        dir={localeDetails.direction}
                        disabled={
                          !catalog.initialReleaseComplete ||
                          publishing ||
                          loading
                        }
                        aria-invalid={Boolean(fieldError)}
                        aria-describedby={
                          fieldError ? `translation-error-${entry.key}` : undefined
                        }
                      />
                      {resetPending && (
                        <p className="ui-translations__field-note" role="status">
                          The generated baseline will replace this value when published.
                        </p>
                      )}
                      {fieldError && (
                        <p
                          id={`translation-error-${entry.key}`}
                          className="ui-translations__field-error"
                          role="alert"
                        >
                          {fieldError}
                        </p>
                      )}
                    </td>
                    <td data-label="Status and actions">
                      <div className="ui-translations__badges">
                        <span className={`ui-translations__badge ui-translations__badge--${entry.origin.toLowerCase()}`}>
                          {entry.ownership === 'HUMAN'
                            ? 'Human edited'
                            : entry.ownership === 'GENERATED'
                              ? 'Generated'
                              : 'Missing translation'}
                        </span>
                        {entry.reviewNeeded && (
                          <span className="ui-translations__badge ui-translations__badge--review">
                            Review needed
                          </span>
                        )}
                        {change && (
                          <span className="ui-translations__badge ui-translations__badge--local">
                            Local change
                          </span>
                        )}
                      </div>
                      <div className="ui-translations__row-actions">
                        <button
                          type="button"
                          className="ui-translations__text-button"
                          onClick={() => handleResetField(entry)}
                          disabled={!change || publishing}
                        >
                          Reset field
                        </button>
                        {entry.ownership === 'HUMAN' &&
                          entry.generatedBaselineAvailable &&
                          catalog.initialReleaseComplete && (
                            <button
                              type="button"
                              className="ui-translations__text-button"
                              onClick={() => handleResetToGenerated(entry)}
                              disabled={resetPending || publishing}
                            >
                              Reset to generated translation
                            </button>
                          )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {catalog && !visibleEntries.length && (
        <div className="ui-translations__empty" role="status">
          <p>
            {catalog.entries.length
              ? 'No translations match these filters.'
              : 'No UI translation catalog entries are available.'}
          </p>
          {catalog.entries.length > 0 && (
            <button
              type="button"
              className="ui-translations__text-button"
              onClick={clearFilters}
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {catalog && (
        <div className="ui-translations__footer-actions">
          <button
            type="button"
            className="btn ui-translations__button ui-translations__button--secondary"
            onClick={discardChanges}
            disabled={!workspace?.changes.length || publishing}
          >
            Discard local changes
          </button>
          {catalog.initialReleaseComplete && (
            <button
              type="button"
              className="btn ui-translations__button ui-translations__button--primary"
              onClick={openPublishConfirmation}
              disabled={!canPublish}
              data-testid="publish-ui-translations"
            >
              Publish {summary.local} change{summary.local === 1 ? '' : 's'}
            </button>
          )}
        </div>
      )}
    </>
  );

  const renderHistory = () => (
    <section className="ui-translations__history" aria-labelledby="ui-translations-history-title">
      <div className="ui-translations__subheading">
        <div>
          <h2 id="ui-translations-history-title">Revision history</h2>
          <p>
            Inspect immutable releases for {localeDetails.label} · {namespace}.
          </p>
        </div>
        <button
          type="button"
          className="ui-translations__text-button"
          onClick={returnToEditor}
        >
          Back to editor
        </button>
      </div>
      {historyError && (
        <div className="ui-translations__message ui-translations__message--error" role="alert">
          <span>{historyError}</span>
          <button type="button" onClick={() => loadHistory()}>
            Retry
          </button>
        </div>
      )}
      {historyLoading && !historyItems.length && (
        <p className="ui-translations__message" role="status">
          Loading revision history…
        </p>
      )}
      {historyIsCurrent && historyItems.length > 0 && (
        <div className="ui-translations__table-wrap">
          <table className="ui-translations__history-table">
            <thead>
              <tr>
                <th scope="col">Revision</th>
                <th scope="col">Published</th>
                <th scope="col">Actor</th>
                <th scope="col">Changes</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {historyItems.map((item) => (
                <tr key={item.releaseId || item.revision}>
                  <td data-label="Revision">
                    {item.revision}
                    {item.live || item.revision === catalog?.releaseRevision
                      ? ' · Live'
                      : ''}
                  </td>
                  <td data-label="Published">{formatDate(item.createdAt)}</td>
                  <td data-label="Actor">{formatActor(item.actor)}</td>
                  <td data-label="Changes">{item.changedKeys.length}</td>
                  <td data-label="Actions">
                    <div className="ui-translations__row-actions">
                      <button
                        type="button"
                        className="ui-translations__text-button"
                        onClick={() => openHistoryDetail(item)}
                        disabled={historyLoading || !historyIsCurrent}
                      >
                        View
                      </button>
                      {admin && catalog?.initialReleaseComplete && (
                        <button
                          type="button"
                          className="ui-translations__text-button"
                          onClick={() => openRollback(item)}
                          disabled={
                            historyLoading ||
                            !historyIsCurrent ||
                            storageUnavailable ||
                            item.revision === catalog.releaseRevision
                          }
                        >
                          Rollback
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!historyLoading &&
        (!historyIsCurrent || !historyItems.length) &&
        !historyError && (
        <p className="ui-translations__empty" role="status">
          No published revisions are available for this selection.
        </p>
      )}
      {historyIsCurrent && historyCursor && (
        <button
          type="button"
          className="btn ui-translations__button ui-translations__button--secondary"
          onClick={() => loadHistory(historyCursor)}
          disabled={historyLoading}
        >
          {historyLoading ? 'Loading…' : 'Load more'}
        </button>
      )}
    </section>
  );

  const renderHistoryDetail = () => (
    <section className="ui-translations__history-detail" aria-labelledby="ui-translations-detail-title">
      <div className="ui-translations__subheading">
        <div>
          <h2 id="ui-translations-detail-title">
            Revision {historyDetail?.revision || ''}
          </h2>
          <p>Immutable publication detail and changed-key summary.</p>
        </div>
        <button
          type="button"
          className="ui-translations__text-button"
          onClick={returnToHistory}
        >
          Back to history
        </button>
      </div>
      {historyDetailIsCurrent && historyDetail && (
        <>
          <dl className="ui-translations__detail-grid">
            <div><dt>Published</dt><dd>{formatDate(historyDetail.createdAt)}</dd></div>
            <div><dt>Actor</dt><dd>{formatActor(historyDetail.actor)}</dd></div>
            <div><dt>Reason</dt><dd>{historyDetail.reason || '—'}</dd></div>
            <div><dt>Catalog</dt><dd>{historyDetail.catalogVersion || '—'}</dd></div>
            <div><dt>Release ID</dt><dd>{historyDetail.releaseId || '—'}</dd></div>
            <div><dt>Previous release</dt><dd>{historyDetail.previousReleaseId || '—'}</dd></div>
          </dl>
          <section className="ui-translations__detail-section">
            <h3>Changed keys</h3>
            {historyDetail.changedKeys.length ? (
              <ul>
                {historyDetail.changedKeys.map((change, index) => (
                  <li key={`${formatChangedKey(change)}-${index}`}>
                    {formatChangedKey(change)}
                  </li>
                ))}
              </ul>
            ) : (
              <p>No changed-key summary was recorded.</p>
            )}
          </section>
          {historyDetail.snapshots.length > 0 && (
            <section className="ui-translations__detail-section">
              <h3>Snapshot summary</h3>
              <ul>
                {historyDetail.snapshots.map((snapshot, index) => (
                  <li key={`${snapshot?.locale || 'snapshot'}-${index}`}>
                    {[snapshot?.locale, snapshot?.namespace, snapshot?.contentHash]
                      .filter(Boolean)
                      .join(' · ') || 'Snapshot'}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </section>
  );

  const modifiedCount = (workspace?.changes || []).filter(
    (change) =>
      change.action === 'SET_VALUE' &&
      Boolean(entryByKey.get(change.key)?.currentValue)
  ).length;
  const addedCount = (workspace?.changes || []).filter(
    (change) =>
      change.action === 'SET_VALUE' &&
      !entryByKey.get(change.key)?.currentValue
  ).length;
  const ownershipResetCount = (workspace?.changes || []).filter(
    (change) => change.action === 'RESET_TO_GENERATED'
  ).length;

  return (
    <>
      <Header />
      <div className="wrapper">
        <Sidebar />
        <main className="content_wrapper ui-translations-page">
          <section className="ui-translations" aria-labelledby="ui-translations-title">
            <div className="ui-translations__heading">
              <div>
                <h1 id="ui-translations-title">UI Translations</h1>
                <p>
                  Correct public Blog interface copy. English source values are read-only.
                </p>
              </div>
              {view === 'editor' && (
                <button
                  type="button"
                  className="ui-translations__text-button"
                  onClick={openHistory}
                >
                  Revision history
                </button>
              )}
            </div>

            {catalog && (
              <div className="ui-translations__release" aria-label="Live release metadata">
                <span>Live revision {formatRevision(catalog.releaseRevision)}</span>
                <span>Catalog {catalog.catalogVersion}</span>
              </div>
            )}

            {catalog && !catalog.initialReleaseComplete && (
              <div className="ui-translations__message ui-translations__message--warning" role="status" data-testid="initial-release-required">
                The first multilingual release is not complete. Public pages currently use
                English fallback. Codex or Claude Code must publish all nine target
                languages through the automation API before staff publish, reset, or
                rollback actions are available.
              </div>
            )}
            {error && (
              <div className="ui-translations__message ui-translations__message--error" role="alert">
                <span>
                  {error} Your existing local changes are safe in this browser.
                </span>
                <button type="button" onClick={loadCatalog} disabled={loading}>
                  Retry
                </button>
              </div>
            )}
            {storageWarning && (
              <div className="ui-translations__message ui-translations__message--warning" role="alert">
                {storageWarning}
              </div>
            )}
            {notice && (
              <div className="ui-translations__message" role="status">
                <span>{notice}</span>
                {hasUnresolvedConflicts && (
                  <button type="button" onClick={() => setConflictOpen(true)}>
                    Resolve conflict{conflicts.length === 1 ? '' : 's'}
                  </button>
                )}
              </div>
            )}
            {hasUnresolvedConflicts && !notice && (
              <div className="ui-translations__message ui-translations__message--warning" role="alert">
                <span>
                  {conflicts.length} unresolved publication conflict
                  {conflicts.length === 1 ? '' : 's'} must be resolved.
                </span>
                <button type="button" onClick={() => setConflictOpen(true)}>
                  Resume resolution
                </button>
              </div>
            )}

            <div className="ui-translations__surface">
              {view === 'editor' && renderEditor()}
              {view === 'history' && renderHistory()}
              {view === 'history-detail' && renderHistoryDetail()}
            </div>
          </section>
        </main>
      </div>

      <Modal
        isOpen={publishOpen}
        toggle={() => !publishing && setPublishOpen(false)}
        centered
        className="ui-translations-modal"
      >
        <ModalHeader toggle={() => !publishing && setPublishOpen(false)}>
          Publish {localeDetails.label} translations
        </ModalHeader>
        <ModalBody>
          <p>
            {summary.local} changed entr{summary.local === 1 ? 'y' : 'ies'} will
            create a new production revision.
          </p>
          <p>
            {Math.max(0, summary.total - summary.local)} unchanged entries and
            their ownership will remain exactly as they are.
          </p>
          <dl className="ui-translations-modal__summary">
            <div><dt>Modified</dt><dd>{modifiedCount}</dd></div>
            <div><dt>Added</dt><dd>{addedCount}</dd></div>
            <div><dt>Ownership resets</dt><dd>{ownershipResetCount}</dd></div>
          </dl>
          {publishError && (
            <p className="ui-translations__field-error" role="alert">
              {publishError}
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="btn ui-translations__button ui-translations__button--secondary"
            onClick={() => setPublishOpen(false)}
            disabled={publishing}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn ui-translations__button ui-translations__button--primary"
            onClick={() => submitPublication(workspace)}
            disabled={
              publishing ||
              !workspace?.changes.length ||
              workspaceConcurrencyConflict
            }
          >
            {publishing ? 'Publishing…' : 'Publish'}
          </button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={conflictOpen}
        toggle={() => !publishing && setConflictOpen(false)}
        centered
        size="lg"
        className="ui-translations-modal"
      >
        <ModalHeader toggle={() => !publishing && setConflictOpen(false)}>
          Resolve publication conflict
        </ModalHeader>
        <ModalBody>
          <p>
            Another staff member changed these keys after your local work began.
          </p>
          {conflicts.map((conflict, index) => (
            <fieldset key={conflict.key} className="ui-translations-modal__conflict">
              <legend>{conflict.key}</legend>
              <dl>
                <div><dt>English source</dt><dd>{conflict.source || '—'}</dd></div>
                <div><dt>Your local value</dt><dd dir={localeDetails.direction}>{conflict.localValue === '' ? 'Empty value' : conflict.localValue || '—'}</dd></div>
                <div><dt>Current production value</dt><dd dir={localeDetails.direction}>{conflict.currentValue || 'English fallback'}</dd></div>
              </dl>
              <label>
                <input
                  type="radio"
                  name={`conflict-${index}`}
                  value="mine"
                  checked={conflictChoices[conflict.key] === 'mine'}
                  onChange={() =>
                    setConflictChoices((current) => ({
                      ...current,
                      [conflict.key]: 'mine',
                    }))
                  }
                  disabled={conflict.removedFromCatalog}
                />{' '}
                Keep mine
              </label>
              <label>
                <input
                  type="radio"
                  name={`conflict-${index}`}
                  value="production"
                  checked={conflictChoices[conflict.key] === 'production'}
                  onChange={() =>
                    setConflictChoices((current) => ({
                      ...current,
                      [conflict.key]: 'production',
                    }))
                  }
                />{' '}
                Use production
              </label>
              {conflict.removedFromCatalog && (
                <p className="ui-translations__field-note">
                  This key left the current catalog, so only production can be used.
                </p>
              )}
            </fieldset>
          ))}
          <p>
            {conflicts.length} conflict{conflicts.length === 1 ? '' : 's'} ·
            Changes to other keys can be merged safely.
          </p>
          {conflictError && (
            <p className="ui-translations__field-error" role="alert">
              {conflictError}
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="btn ui-translations__button ui-translations__button--secondary"
            onClick={() => setConflictOpen(false)}
            disabled={publishing}
          >
            Cancel and keep local work
          </button>
          <button
            type="button"
            className="btn ui-translations__button ui-translations__button--primary"
            onClick={resolveAndPublish}
            disabled={publishing}
          >
            {publishing ? 'Publishing…' : 'Resolve and publish'}
          </button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={Boolean(rollbackItem)}
        toggle={() => !rollingBack && setRollbackItem(null)}
        centered
        className="ui-translations-modal"
      >
        <ModalHeader toggle={() => !rollingBack && setRollbackItem(null)}>
          Roll back {localeDetails.label}
        </ModalHeader>
        <ModalBody>
          <p>
            Restore {localeDetails.label} from revision {rollbackItem?.revision}.
            This creates a new revision, keeps every other language current, and
            does not delete history.
          </p>
          {rollbackError && (
            <p className="ui-translations__field-error" role="alert">
              {rollbackError}
            </p>
          )}
          {rollbackAttemptConflict && (
            <button
              type="button"
              className="btn ui-translations__button ui-translations__button--secondary"
              onClick={clearConflictingRollbackAttempt}
              disabled={rollingBack}
            >
              Clear local retry state
            </button>
          )}
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="btn ui-translations__button ui-translations__button--secondary"
            onClick={() => setRollbackItem(null)}
            disabled={rollingBack}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn ui-translations__button ui-translations__button--primary"
            onClick={confirmRollback}
            disabled={rollingBack || storageUnavailable || !admin}
          >
            {rollingBack ? 'Rolling back…' : 'Create rollback revision'}
          </button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default UiTranslations;
